import React, { useState, useEffect } from 'react';
import { 
  Users, 
  MapPin, 
  Clock, 
  DollarSign, 
  Send, 
  Upload, 
  Briefcase, 
  GraduationCap, 
  Star, 
  CheckCircle, 
  Calendar, 
  ArrowRight, 
  Eye,
  AlertCircle
} from 'lucide-react';
import { dbHelpers } from '../config/database';
import { jobOpenings as defaultJobOpenings } from '../data/jobOpenings';

interface JobOpening {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  experience: string;
  salary: string;
  skills: string[];
  description: string;
  requirements: string[];
  responsibilities: string[];
  benefits: string[];
  is_active: boolean;
  posted_date: string;
  application_deadline: string;
}

const Careers = () => {
  const [jobOpenings, setJobOpenings] = useState<JobOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    currentSalary: '',
    expectedSalary: '',
    noticePeriod: '',
    location: '',
    resume: null as File | null,
    coverLetter: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedJob, setSelectedJob] = useState<JobOpening | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);

  useEffect(() => {
    loadJobOpenings();
  }, []);

  const loadJobOpenings = async () => {
    try {
      const jobs = await dbHelpers.getActiveJobs();
      let rawJobs: any[] = [];
      if (jobs && Array.isArray(jobs)) {
        rawJobs = jobs;
      } else if (jobs && jobs.status !== false && Array.isArray(jobs.jobs)) {
        rawJobs = jobs.jobs;
      } else if (jobs && jobs.status !== false && Array.isArray(jobs.data)) {
        rawJobs = jobs.data;
      }

      if (rawJobs.length > 0) {
        const normalized = rawJobs.map((j: any) => {
          let parsedSkills: string[] = [];
          if (typeof j.skills === 'string') {
            parsedSkills = j.skills.split(',').map((s: string) => s.trim()).filter(Boolean);
          } else if (Array.isArray(j.skills)) {
            parsedSkills = j.skills;
          }

          let jobType = j.job_type || j.type || 'Full-Time';
          if (jobType === 'full_time') jobType = 'Full-Time';
          else if (jobType === 'part_time') jobType = 'Part-Time';
          else if (jobType === 'contract') jobType = 'Contract';
          else if (jobType === 'internship') jobType = 'Internship';
          else if (jobType === 'temporary') jobType = 'Temporary';

          let salaryStr = 'Competitive';
          if (j.salary_from && j.salary_to) {
            salaryStr = `₹${Number(j.salary_from).toLocaleString()} - ₹${Number(j.salary_to).toLocaleString()} / year`;
          } else if (j.salary_range) {
            salaryStr = j.salary_range;
          } else if (j.salary) {
            salaryStr = j.salary;
          }

          const parseJsonArray = (val: any) => {
            if (Array.isArray(val)) return val;
            if (typeof val === 'string') {
              try {
                const parsed = JSON.parse(val);
                if (Array.isArray(parsed)) return parsed;
              } catch (e) {
                return [val];
              }
            }
            return [];
          };

          return {
            id: String(j.id),
            title: j.title || j.job_title || '',
            department: j.department || 'General',
            location: j.location || 'Gwalior / Remote',
            type: jobType,
            experience: j.experience_required || j.experience || 'Not specified',
            salary: salaryStr,
            skills: parsedSkills,
            description: j.description || '',
            requirements: parseJsonArray(j.requirements),
            responsibilities: parseJsonArray(j.responsibilities),
            benefits: parseJsonArray(j.benefits),
            is_active: Number(j.is_active) === 1,
            posted_date: j.created_at || j.posted_date || new Date().toISOString(),
            application_deadline: j.application_deadline || ''
          };
        });

        setJobOpenings(normalized);
      } else {
        // Fallback to rich catalog from jobOpenings.ts so positions are always accessible
        setJobOpenings(defaultJobOpenings.map(d => ({
          id: String(d.id),
          title: d.title,
          department: d.department,
          location: d.location,
          type: d.type,
          experience: d.experience,
          salary: d.salary,
          skills: d.skills,
          description: d.description,
          requirements: d.requirements,
          responsibilities: d.responsibilities,
          benefits: d.benefits,
          is_active: d.isActive,
          posted_date: d.postedDate,
          application_deadline: d.applicationDeadline
        })));
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
      // Fallback
      setJobOpenings(defaultJobOpenings.map(d => ({
        id: String(d.id),
        title: d.title,
        department: d.department,
        location: d.location,
        type: d.type,
        experience: d.experience,
        salary: d.salary,
        skills: d.skills,
        description: d.description,
        requirements: d.requirements,
        responsibilities: d.responsibilities,
        benefits: d.benefits,
        is_active: d.isActive,
        posted_date: d.postedDate,
        application_deadline: d.applicationDeadline
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file && file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit. Please upload a smaller file.');
      e.target.value = '';
      return;
    }
    setFormData(prev => ({
      ...prev,
      resume: file
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    
    try {
      // 1. Upload resume if present via dedicated public resume endpoint
      let resumeUploadId = 0;
      if (formData.resume) {
        try {
          const uploadRes = await dbHelpers.uploadResume(formData.resume);
          if (uploadRes && uploadRes.status && uploadRes.id) {
            resumeUploadId = Number(uploadRes.id);
          } else if (uploadRes && uploadRes.message) {
            console.warn('Resume upload notice:', uploadRes.message);
          }
        } catch (uploadErr: any) {
          console.warn('Resume upload error, proceeding with application details:', uploadErr);
        }
      }

      // 2. Find selected job ID and determine position title
      let jobIdNum = 0;
      let appliedPosition = formData.position;
      if (formData.position && formData.position !== 'other') {
        const foundJob = jobOpenings.find(j => j.title === formData.position);
        if (foundJob) {
          appliedPosition = foundJob.title;
          const parsed = parseInt(foundJob.id, 10);
          jobIdNum = isNaN(parsed) ? 0 : parsed;
        }
      } else if (formData.position === 'other') {
        appliedPosition = 'Other / Speculative Application';
      }

      // 3. Save application details to database via API
      const applicationData = {
        job_id: jobIdNum,
        position_applied: appliedPosition,
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        location: formData.location.trim(),
        experience_years: parseFloat(formData.experience) || 0,
        current_salary: parseFloat(formData.currentSalary.replace(/[^0-9.]/g, '')) || 0,
        expected_salary: parseFloat(formData.expectedSalary.replace(/[^0-9.]/g, '')) || 0,
        notice_period: formData.noticePeriod,
        cover_letter: formData.coverLetter.trim(),
        resume_upload_id: resumeUploadId
      };

      const res = await dbHelpers.applyToJob(applicationData);
      
      if (!res || !res.status) {
        throw new Error(res?.message || 'Application submission failed. Please try again.');
      }
      
      setSubmitStatus('success');
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        position: '',
        experience: '',
        currentSalary: '',
        expectedSalary: '',
        noticePeriod: '',
        location: '',
        resume: null,
        coverLetter: ''
      });
      setSelectedJob(null);

      const fileInput = document.getElementById('resume') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      console.error('Error submitting application:', error);
      setSubmitStatus('error');
      setErrorMessage(error?.message || 'There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApplyClick = (job: JobOpening) => {
    setFormData(prev => ({ 
      ...prev, 
      position: job.title,
      location: prev.location || job.location
    }));
    setSelectedJob(job);
    const formEl = document.getElementById('application-form');
    if (formEl) {
      formEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleViewDetails = (job: JobOpening) => {
    setSelectedJob(job);
    setShowJobDetails(true);
  };

  const benefits = [
    {
      icon: <DollarSign className="h-6 w-6" />,
      title: 'Competitive Salary',
      description: 'Industry-leading compensation packages with performance bonuses'
    },
    {
      icon: <GraduationCap className="h-6 w-6" />,
      title: 'Learning & Development',
      description: 'Continuous learning opportunities, certifications, and conference attendance'
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: 'Flexible Working',
      description: 'Hybrid work model with flexible hours and work-life balance'
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Great Team Culture',
      description: 'Collaborative environment with talented professionals and team events'
    }
  ];

  return (
    <section id="careers" className="py-20 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-secondary mb-4">
            Join Our Team
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Be part of an innovative team that's shaping the future of technology. 
            We're always looking for talented individuals who are passionate about making a difference.
          </p>
        </div>

        {/* Benefits */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-brand-secondary text-center mb-8 animate-fade-in-up">
            Why Work With Us?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up group"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="text-brand-primary mb-4 flex justify-center group-hover:animate-bounce">
                  {benefit.icon}
                </div>
                <h4 className="text-lg font-semibold text-brand-secondary mb-2">
                  {benefit.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Job Openings */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-brand-secondary text-center mb-8 animate-fade-in-up">
            Current Openings ({jobOpenings.length} positions available)
          </h3>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Loading job openings...</p>
            </div>
          ) : jobOpenings.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 shadow-md max-w-2xl mx-auto space-y-3">
              <Briefcase className="h-12 w-12 text-gray-400 mx-auto animate-pulse" />
              <h4 className="text-lg font-bold text-brand-secondary">No Active Openings Right Now</h4>
              <p className="text-gray-500 text-xs max-w-md mx-auto">
                We don't have any open vacancies listed today. Please check back later or submit a speculative inquiry.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobOpenings.map((job, index) => (
                <div 
                  key={job.id} 
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-brand-primary transform hover:scale-105 hover:-translate-y-2 animate-fade-in-up group"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xl font-semibold text-brand-secondary group-hover:text-brand-primary transition-colors duration-200">
                        {job.title}
                      </h4>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium animate-pulse">
                        New
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Briefcase className="h-4 w-4 mr-1 text-brand-primary" />
                      <span>{job.department}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <MapPin className="h-4 w-4 mr-1 text-brand-primary" />
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <Clock className="h-4 w-4 mr-1 text-brand-primary" />
                      <span>{job.type} • {job.experience}</span>
                    </div>
                    <div className="flex items-center text-sm text-brand-primary font-medium mb-2">
                      <DollarSign className="h-4 w-4 mr-1" />
                      <span>{job.salary}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar className="h-3 w-3 mr-1" />
                      <span>Posted: {new Date(job.posted_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {job.description}
                  </p>
                  
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-brand-secondary mb-2">Required Skills:</h5>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.slice(0, 3).map((skill, skillIndex) => (
                        <span key={skillIndex} className="bg-brand-light text-brand-primary px-2 py-1 rounded text-xs font-medium">
                          {skill}
                        </span>
                      ))}
                      {job.skills.length > 3 && (
                        <span className="text-xs text-gray-500">+{job.skills.length - 3} more</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleViewDetails(job)}
                      className="flex-1 bg-gray-100 text-brand-secondary py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center group"
                    >
                      <Eye className="h-4 w-4 mr-1 group-hover:animate-pulse" />
                      View Details
                    </button>
                    <button 
                      onClick={() => handleApplyClick(job)}
                      className="flex-1 bg-brand-primary text-white py-2 px-4 rounded-lg font-medium hover:bg-brand-dark transition-all duration-200 flex items-center justify-center group transform hover:scale-105"
                    >
                      Apply Now
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform duration-200" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Job Details Modal */}
        {showJobDetails && selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-brand-secondary">{selectedJob.title}</h3>
                    <p className="text-brand-primary font-medium">{selectedJob.department} • {selectedJob.location}</p>
                  </div>
                  <button 
                    onClick={() => setShowJobDetails(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-brand-secondary mb-3">Job Description</h4>
                  <p className="text-gray-600 leading-relaxed">{selectedJob.description}</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-brand-secondary mb-3">Requirements</h4>
                  <ul className="space-y-2">
                    {selectedJob.requirements.map((req, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-brand-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-brand-secondary mb-3">Responsibilities</h4>
                  <ul className="space-y-2">
                    {selectedJob.responsibilities.map((resp, index) => (
                      <li key={index} className="flex items-start">
                        <ArrowRight className="h-5 w-5 text-brand-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-brand-secondary mb-3">Skills Required</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.skills.map((skill, index) => (
                      <span key={index} className="bg-brand-light text-brand-primary px-3 py-1 rounded-full text-sm font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-brand-primary font-semibold">{selectedJob.experience}</div>
                      <div className="text-sm text-gray-600">Experience</div>
                    </div>
                    <div>
                      <div className="text-brand-primary font-semibold">{selectedJob.salary}</div>
                      <div className="text-sm text-gray-600">Salary</div>
                    </div>
                    <div>
                      <div className="text-brand-primary font-semibold">{selectedJob.type}</div>
                      <div className="text-sm text-gray-600">Type</div>
                    </div>
                    <div>
                      <div className="text-brand-primary font-semibold">{new Date(selectedJob.application_deadline).toLocaleDateString()}</div>
                      <div className="text-sm text-gray-600">Deadline</div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <button 
                    onClick={() => {
                      setShowJobDetails(false);
                      handleApplyClick(selectedJob);
                    }}
                    className="flex-1 bg-brand-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-brand-dark transition-colors duration-200 flex items-center justify-center"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    Apply for this Position
                  </button>
                  <button 
                    onClick={() => setShowJobDetails(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Application Form */}
        <div id="application-form" className="bg-white rounded-xl p-8 shadow-lg border border-gray-200 animate-fade-in-up">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-brand-secondary mb-4">Apply for a Position</h3>
            <p className="text-gray-600">
              Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </div>

          {selectedJob && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-600 text-white rounded-lg shrink-0">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Position Selected</p>
                  <p className="text-sm font-extrabold text-slate-800">{selectedJob.title} <span className="font-medium text-slate-500">({selectedJob.department} • {selectedJob.location})</span></p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedJob(null);
                  setFormData(prev => ({ ...prev, position: '' }));
                }}
                className="text-xs text-emerald-700 hover:text-emerald-900 font-bold underline text-left sm:text-right"
              >
                Change Selection
              </button>
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center animate-fade-in">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 shrink-0" />
              <span className="text-green-800 font-medium">Application submitted successfully! We'll contact you soon.</span>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-fade-in">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-800 text-sm">Submission Notice</p>
                <p className="text-red-700 text-xs mt-0.5">{errorMessage || 'There was an error submitting your application. Please check your inputs and try again.'}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="animate-fade-in-up">
              <h4 className="text-lg font-semibold text-brand-secondary mb-4">Personal Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-brand-secondary mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-brand-secondary mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-brand-secondary mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-brand-secondary mb-2">
                    Current Location *
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                    placeholder="Enter your current location"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="animate-fade-in-up animation-delay-200">
              <h4 className="text-lg font-semibold text-brand-secondary mb-4">Professional Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-brand-secondary mb-2">
                    Position Applied For *
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={(e) => {
                      handleInputChange(e);
                      const sel = jobOpenings.find(j => j.title === e.target.value);
                      setSelectedJob(sel || null);
                    }}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                  >
                    <option value="">Select a position</option>
                    {jobOpenings.map((job) => (
                      <option key={job.id} value={job.title}>{job.title} ({job.department} - {job.location})</option>
                    ))}
                    {formData.position && !jobOpenings.some(j => j.title === formData.position) && formData.position !== 'other' && (
                      <option value={formData.position}>{formData.position}</option>
                    )}
                    <option value="other">Other / Speculative Application</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-brand-secondary mb-2">
                    Total Experience *
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                  >
                    <option value="">Select experience</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-8">5-8 years</option>
                    <option value="8-12">8-12 years</option>
                    <option value="12+">12+ years</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="currentSalary" className="block text-sm font-medium text-brand-secondary mb-2">
                    Current Salary (LPA)
                  </label>
                  <input
                    type="text"
                    id="currentSalary"
                    name="currentSalary"
                    value={formData.currentSalary}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                    placeholder="e.g., 12 LPA"
                  />
                </div>
                <div>
                  <label htmlFor="expectedSalary" className="block text-sm font-medium text-brand-secondary mb-2">
                    Expected Salary (LPA) *
                  </label>
                  <input
                    type="text"
                    id="expectedSalary"
                    name="expectedSalary"
                    value={formData.expectedSalary}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                    placeholder="e.g., 15 LPA"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="noticePeriod" className="block text-sm font-medium text-brand-secondary mb-2">
                    Notice Period *
                  </label>
                  <select
                    id="noticePeriod"
                    name="noticePeriod"
                    value={formData.noticePeriod}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                  >
                    <option value="">Select notice period</option>
                    <option value="immediate">Immediate</option>
                    <option value="15-days">15 days</option>
                    <option value="1-month">1 month</option>
                    <option value="2-months">2 months</option>
                    <option value="3-months">3 months</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Resume Upload */}
            <div className="animate-fade-in-up animation-delay-400">
              <label htmlFor="resume" className="block text-sm font-medium text-brand-secondary mb-2">
                Resume/CV * (PDF, DOC, DOCX - Max 5MB)
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="resume"
                  name="resume"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 hover:border-brand-primary"
                />
                <Upload className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
              </div>
              {formData.resume && (
                <p className="mt-2 text-sm text-brand-primary animate-fade-in">
                  Selected: {formData.resume.name}
                </p>
              )}
            </div>

            {/* Cover Letter */}
            <div className="animate-fade-in-up animation-delay-600">
              <label htmlFor="coverLetter" className="block text-sm font-medium text-brand-secondary mb-2">
                Cover Letter / Why do you want to join X2InfoTech?
              </label>
              <textarea
                id="coverLetter"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 resize-vertical hover:border-brand-primary"
                placeholder="Tell us about yourself and why you're interested in this position..."
              ></textarea>
            </div>

            {/* Submit Button */}
            <div className="animate-fade-in-up animation-delay-800">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white py-3.5 px-6 rounded-xl font-bold shadow-lg hover:shadow-[0_8px_25px_rgba(20,201,203,0.35)] transition-all duration-200 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.01] active:scale-[0.99]"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting Application...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                    Submit Application
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Careers;